
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Terminal from 'plaid-threads/Icons/Terminal';
export const metadata = {
  toc: true,
  layout: 'reference',
  description: 'Reference for integrating with the Link Flutter SDK. Includes requirements, compatibility information, and steps for getting started.'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Link Flutter SDK' subtitle='Reference for integrating with the Link Flutter SDK' mdxType="Header" />
    <p>{`This guide covers the Link Flutter SDK, version 1.x.x. If you are moving from the community `}<inlineCode parentName="p">{`plaid_flutter`}</inlineCode>{` package, see `}<a parentName="p" {...{
        "href": "#migrating-from-the-community-flutter-sdk"
      }}>{`Migrating from the community Flutter SDK`}</a>{`.`}</p>
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <Callout icon={<Terminal mdxType="Terminal" />} mdxType="Callout">
      <p>{`Prefer to learn with code examples? A `}<a parentName="p" {...{
          "href": "https://github.com/plaid/plaid-link-flutter"
        }}>{`GitHub repo`}</a>{` showing a working example Link implementation is available for this topic.`}</p>
    </Callout>
    <p>{`The Plaid Link Flutter SDK is the official Flutter plugin for opening Link in iOS and Android apps. The SDK wraps Plaid's native iOS and Android SDKs and supports regular Link, Layer, Headless Link, Embedded Institution Search, and iOS FinanceKit flows.`}</p>
    <p>{`Flutter web is not supported. For web apps, use the `}<a parentName="p" {...{
        "href": "/docs/link/web"
      }}>{`Link Web SDK`}</a>{`.`}</p>
    <p>{`To get started with `}<a parentName="p" {...{
        "href": "https://github.com/plaid/plaid-link-flutter"
      }}>{`Plaid Link for Flutter`}</a>{` you'll want to sign up for `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/keys"
      }}>{`free API keys`}</a>{` through the Plaid Dashboard.`}</p>
    <h4 {...{
      "id": "requirements"
    }}>{`Requirements`}</h4>
    <ul>
      <li parentName="ul">{`Flutter `}<inlineCode parentName="li">{`3.29.3`}</inlineCode>{` or higher`}</li>
      <li parentName="ul">{`Dart `}<inlineCode parentName="li">{`3.7`}</inlineCode>{` or higher`}</li>
      <li parentName="ul">{`iOS `}<inlineCode parentName="li">{`15.0`}</inlineCode>{` or higher`}</li>
      <li parentName="ul">{`Android `}<inlineCode parentName="li">{`minSdk`}</inlineCode>{` 26 or higher`}</li>
    </ul>
    <blockquote>
      <p parentName="blockquote">{`New versions of the `}<a parentName="p" {...{
          "href": "https://github.com/plaid/plaid-link-flutter"
        }}>{`Flutter SDK`}</a>{` are released frequently. Major releases occur annually. The Link SDK uses Semantic Versioning, ensuring that all non-major releases are non-breaking, backwards-compatible updates. We recommend you update regularly (at least once a quarter, and ideally once a month) to ensure the best Plaid Link experience in your application.`}</p>
    </blockquote>
    <p>{`SDK versions are supported for two years; with each major SDK release, Plaid will stop officially supporting any previous SDK versions that are more than two years old. While these older versions are expected to continue to work without disruption, Plaid will not provide assistance with unsupported SDK versions.`}</p>
    <h5 {...{
      "id": "version-compatibility"
    }}>{`Version Compatibility`}</h5>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Flutter SDK`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Android SDK`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`iOS SDK`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Status`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`1.2.x`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`6.3.0`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`7.2.2`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Active, supports Xcode 16.1`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`1.1.x`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`6.2.2`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`7.2.0`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Active, supports Xcode 16.1`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`1.0.x`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`6.2.0`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`7.1.0`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Active, supports Xcode 16.1`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "getting-started"
    }}>{`Getting Started`}</h4>
    <h5 {...{
      "id": "installing-the-sdk"
    }}>{`Installing the SDK`}</h5>
    <p>{`In your Flutter project directory, run:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false",
        "switcher": "false"
      }}>{`flutter pub add plaid_link_flutter
`}</code></pre>
    <p>{`Or add the package to your `}<inlineCode parentName="p">{`pubspec.yaml`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-yaml",
        "metastring": "switcher=false",
        "switcher": "false"
      }}>{`dependencies:
  plaid_link_flutter: ^1.0.0
`}</code></pre>
    <p>{`Then import it:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-dart",
        "metastring": "switcher=false",
        "switcher": "false"
      }}>{`import 'package:plaid_link_flutter/plaid_link_flutter.dart';
`}</code></pre>
    <h5 {...{
      "id": "ios-setup"
    }}>{`iOS Setup`}</h5>
    <p>{`Set your host app deployment target to iOS 15.0 or later:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ruby",
        "metastring": "switcher=false #ios/Podfile",
        "switcher": "false",
        "#ios/Podfile": true
      }}>{`platform :ios, '15.0'
`}</code></pre>
    <p>{`Then install pods from your host app:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false",
        "switcher": "false"
      }}>{`cd ios && pod install
`}</code></pre>
    <p>{`The Flutter SDK 1.2.x vendors LinkKit `}<inlineCode parentName="p">{`7.2.2`}</inlineCode>{` as an iOS device and simulator framework. Mac Catalyst is not supported.`}</p>
    <p>{`Xcode 16.1 or higher is required.`}</p>
    <p>{`For Identity Verification flows, add `}<inlineCode parentName="p">{`NSCameraUsageDescription`}</inlineCode>{` to your app's `}<inlineCode parentName="p">{`Info.plist`}</inlineCode>{` if your Link token can require document or selfie capture.`}</p>
    <h5 {...{
      "id": "android-setup"
    }}>{`Android Setup`}</h5>
    <p>{`Set your host app `}<inlineCode parentName="p">{`minSdk`}</inlineCode>{` to 26 or later:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-kotlin",
        "metastring": "switcher=false #android/app/build.gradle.kts",
        "switcher": "false",
        "#android/app/build.gradle.kts": true
      }}>{`android {
  defaultConfig {
    minSdk = 26
  }
}
`}</code></pre>
    <p>{`The Flutter SDK 1.2.x depends on the Android Link SDK `}<inlineCode parentName="p">{`6.3.0`}</inlineCode>{`. Most apps do not need additional ProGuard or R8 rules. If your release build uses custom aggressive shrinking and strips the Flutter plugin, keep the plugin package:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-proguard",
        "metastring": "switcher=false",
        "switcher": "false"
      }}>{`-keep class com.plaid.plaid_link_flutter.** { *; }
`}</code></pre>
    <p>{`For Identity Verification flows, add the camera and audio permissions required by your configured Plaid products to the host app manifest.`}</p>
    <ul>
      <li parentName="ul">{`Register your Android package name in the `}<a parentName="li" {...{
          "href": "https://dashboard.plaid.com/developers/api"
        }}>{`Dashboard`}</a>{`. This is required in order to connect to OAuth institutions (which includes most major banks).`}</li>
    </ul>
    <h6 {...{
      "id": "sample-app"
    }}>{`Sample app`}</h6>
    <p>{`For a sample app that demonstrates a minimal integration with the Flutter SDK, see the `}<a parentName="p" {...{
        "href": "https://github.com/plaid/plaid-link-flutter"
      }}>{`Plaid Link Flutter GitHub repo`}</a>{`.`}</p>
    <h4 {...{
      "id": "opening-link"
    }}>{`Opening Link`}</h4>
    <p>{`Before you can open Link, you need to first create a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{`. A `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` can be configured for different Link flows and is used to control much of Link's behavior. To see how to create a new `}<inlineCode parentName="p">{`link_token`}</inlineCode>{`, see the API Reference entry for `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`.
If your Flutter application will be used on Android, the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` call should include the `}<inlineCode parentName="p">{`android_package_name`}</inlineCode>{` parameter.
Each time you open Link, you will need to get a new `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` from your server.`}</p>
    <p>{`Next, create and retain a `}<inlineCode parentName="p">{`PlaidLinkSession`}</inlineCode>{`, then call `}<inlineCode parentName="p">{`open`}</inlineCode>{` on that session. Create the session as soon as the screen is ready so Link can load before the user taps your button.`}</p>
    <h4 {...{
      "id": "createplaidlinksession"
    }}><inlineCode parentName="h4">{`createPlaidLinkSession()`}</inlineCode></h4>
    <pre><code parentName="pre" {...{
        "className": "language-dart",
        "metastring": "switcher=false",
        "switcher": "false"
      }}>{`PlaidLinkSession? _linkSession;
bool _linkReady = false;

Future<void> createLink(String linkToken) async {
  _linkSession = await createPlaidLinkSession(
    LinkTokenConfiguration(
      token: linkToken,
      onSuccess: (success) {
        handleSuccess(success);
      },
      onExit: (exit) {
        handleExit(exit);
      },
      onEvent: (event) {
        handleEvent(event);
      },
      onLoad: () {
        setState(() => _linkReady = true);
      },
    ),
  );
}
`}</code></pre>
    <p>{`Layer and Headless Link use their own session creation methods: `}<inlineCode parentName="p">{`createPlaidLayerSession`}</inlineCode>{` and `}<inlineCode parentName="p">{`createPlaidHeadlessSession`}</inlineCode>{`. Use a Link token that is compatible with the flow you are creating.`}</p>
    <h4 {...{
      "id": "open"
    }}><inlineCode parentName="h4">{`open()`}</inlineCode></h4>
    <pre><code parentName="pre" {...{
        "className": "language-dart",
        "metastring": "switcher=false",
        "switcher": "false"
      }}>{`Future<void> openLink() async {
  if (!_linkReady) return;
  await _linkSession?.open();
}
`}</code></pre>
    <p>{`To request a fullscreen presentation on iOS, pass `}<inlineCode parentName="p">{`true`}</inlineCode>{` to `}<inlineCode parentName="p">{`open`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-dart",
        "metastring": "switcher=false",
        "switcher": "false"
      }}>{`await _linkSession?.open(true);
`}</code></pre>
    <p>{`The fullscreen flag has no effect on Android, where Link opens in its own Activity.`}</p>
    <h4 {...{
      "id": "plaidlink"
    }}><inlineCode parentName="h4">{`PlaidLink`}</inlineCode></h4>
    <p><inlineCode parentName="p">{`PlaidLink`}</inlineCode>{` is not a widget and is not used to open Link — use the session APIs shown above for that. It exposes two process-wide statics.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`PlaidLink.sdkVersion`}</inlineCode>{` property returns the native Plaid SDK version, not the Flutter package version:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-dart",
        "metastring": "switcher=false",
        "switcher": "false"
      }}>{`final sdkVersion = await PlaidLink.sdkVersion;
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`PlaidLink.onEvent`}</inlineCode>{` stream is available for advanced process-wide event observers. Prefer per-session `}<inlineCode parentName="p">{`onEvent`}</inlineCode>{` callbacks for most integrations.`}</p>
    <h4 {...{
      "id": "onsuccess"
    }}><inlineCode parentName="h4">{`onSuccess`}</inlineCode></h4>
    <pre><code parentName="pre" {...{
        "className": "language-dart",
        "metastring": "switcher=false #onSuccess example",
        "switcher": "false",
        "#onSuccess": true,
        "example": true
      }}>{`Future<void> handleSuccess(LinkSuccess success) async {
  // If using Item-based products, exchange public_token
  // for access_token
  await exchangePublicToken(
    publicToken: success.publicToken,
    linkSessionId: success.metadata.linkSessionId,
  );
}
`}</code></pre>
    <h4 {...{
      "id": "onexit"
    }}><inlineCode parentName="h4">{`onExit`}</inlineCode></h4>
    <pre><code parentName="pre" {...{
        "className": "language-dart",
        "metastring": "switcher=false #onExit example",
        "switcher": "false",
        "#onExit": true,
        "example": true
      }}>{`void handleExit(LinkExit exit) {
  supportHandler.report({
    'error': exit.error,
    'institution': exit.metadata.institution,
    'linkSessionId': exit.metadata.linkSessionId,
    'requestId': exit.metadata.requestId,
    'status': exit.metadata.status,
  });
}
`}</code></pre>
    <h4 {...{
      "id": "onevent"
    }}><inlineCode parentName="h4">{`onEvent`}</inlineCode></h4>
    <p>{`The Flutter SDK emits `}<inlineCode parentName="p">{`onEvent`}</inlineCode>{` callbacks throughout the account linking process. The `}<inlineCode parentName="p">{`HANDOFF`}</inlineCode>{` event can arrive after `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{`; it indicates that Link has handed control back to your app and is useful for conversion analytics.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-dart",
        "metastring": "switcher=false",
        "switcher": "false"
      }}>{`void handleEvent(LinkEvent event) {
  analytics.track('plaid_link_event', {
    'eventName': event.eventName.value,
    'linkSessionId': event.metadata.linkSessionId,
  });
}
`}</code></pre>
    <h4 {...{
      "id": "submit"
    }}><inlineCode parentName="h4">{`submit()`}</inlineCode></h4>
    <p>{`Layer sessions can receive customer data, such as a phone number, before opening Link. Create the session with a Layer-compatible Link token, then call `}<inlineCode parentName="p">{`submit()`}</inlineCode>{` on the retained session.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-dart",
        "metastring": "switcher=false",
        "switcher": "false"
      }}>{`PlaidLayerSession? _layerSession;

Future<void> createLayer(String linkToken) async {
  _layerSession = await createPlaidLayerSession(
    LayerTokenConfiguration(
      token: linkToken,
      onSuccess: (success) {
        handleLayerSuccess(success);
      },
    ),
  );
}

Future<void> submitPhoneNumber() async {
  await _layerSession?.submit(
    const SubmissionData(
      phoneNumber: '+14155550123',
    ),
  );
}
`}</code></pre>
    <h4 {...{
      "id": "dispose"
    }}><inlineCode parentName="h4">{`dispose()`}</inlineCode></h4>
    <p>{`Sessions clean up their own callbacks: success and exit callbacks dispose active listeners automatically. Call `}<inlineCode parentName="p">{`dispose()`}</inlineCode>{` yourself only if you create a session but abandon it before opening.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-dart",
        "metastring": "switcher=false",
        "switcher": "false"
      }}>{`_linkSession?.dispose();
`}</code></pre>
    <h4 {...{
      "id": "oauth"
    }}>{`OAuth`}</h4>
    <p>{`Using Plaid Link with an OAuth flow requires some additional setup instructions. For details, see the `}<a parentName="p" {...{
        "href": "/docs/link/oauth"
      }}>{`OAuth guide`}</a>{`.`}</p>
    <p>{`For iOS, configure your Plaid redirect URI as a Universal Link in the Plaid Dashboard and in your app entitlements. For Android, register your app package name in the Plaid Dashboard and pass `}<inlineCode parentName="p">{`android_package_name`}</inlineCode>{` when creating Link tokens. The Flutter SDK does not expose a separate OAuth resume method; OAuth return handling is managed by the native Plaid SDKs and your app's platform configuration.`}</p>
    <h4 {...{
      "id": "upgrading"
    }}>{`Upgrading`}</h4>
    <p>{`The latest version of the SDK is available from `}<a parentName="p" {...{
        "href": "https://github.com/plaid/plaid-link-flutter"
      }}>{`GitHub`}</a>{`. New versions of the SDK are released frequently. Major releases occur annually. The Link SDK uses Semantic Versioning, ensuring that all non-major releases are non-breaking, backwards-compatible updates. We recommend you update regularly (at least once a quarter, and ideally once a month) to ensure the best Plaid Link experience in your application.`}</p>
    <p>{`SDK versions are supported for two years; with each major SDK release, Plaid will stop officially supporting any previous SDK versions that are more than two years old. While these older versions are expected to continue to work without disruption, Plaid will not provide assistance with unsupported SDK versions.`}</p>
    <h5 {...{
      "id": "migrating-from-the-community-flutter-sdk"
    }}>{`Migrating from the community Flutter SDK`}</h5>
    <p><a parentName="p" {...{
        "href": "https://github.com/jorgefspereira/plaid_flutter"
      }}><inlineCode parentName="a">{`plaid_flutter`}</inlineCode></a>{` is a community-maintained package. It is not built, released, or supported by Plaid. `}<inlineCode parentName="p">{`plaid_link_flutter`}</inlineCode>{` is Plaid's official plugin. It wraps the same native iOS and Android SDKs as Plaid's other mobile SDKs, is covered by Plaid Support, and uses the same session-based API as the React Native, iOS, and Android SDKs.`}</p>
    <p>{`The main change when migrating is the move from a global handler API to explicit session objects. Instead of calling `}<inlineCode parentName="p">{`PlaidLink.create()`}</inlineCode>{` and then `}<inlineCode parentName="p">{`PlaidLink.open()`}</inlineCode>{`, create a session for the flow you want to run, retain that session in your widget state, and call methods on it.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Area`}</th>
          <th parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="th">{`plaid_flutter`}</inlineCode>{` (community)`}</th>
          <th parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="th">{`plaid_link_flutter`}</inlineCode>{` (official)`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Package import`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`package:plaid_flutter/plaid_flutter.dart`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`package:plaid_link_flutter/plaid_link_flutter.dart`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Primary API`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Global `}<inlineCode parentName="td">{`PlaidLink.create()`}</inlineCode>{` and `}<inlineCode parentName="td">{`PlaidLink.open()`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`createPlaidLinkSession()`}</inlineCode>{` returns a `}<inlineCode parentName="td">{`PlaidLinkSession`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Callback model`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Global streams: `}<inlineCode parentName="td">{`PlaidLink.onSuccess`}</inlineCode>{`, `}<inlineCode parentName="td">{`onExit`}</inlineCode>{`, `}<inlineCode parentName="td">{`onEvent`}</inlineCode>{`, `}<inlineCode parentName="td">{`onLoad`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Per-session callbacks passed in the configuration`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Event name field`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`event.name`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`event.eventName`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Layer`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PlaidLink.submit(...)`}</inlineCode>{` on the global object`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PlaidLayerSession.submit(...)`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Headless Link`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Global create/open pattern`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`createPlaidHeadlessSession()`}</inlineCode>{` and `}<inlineCode parentName="td">{`PlaidHeadlessSession.start()`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Embedded search`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PlaidEmbeddedView`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PlaidEmbeddedSearchView`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`OAuth resume`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PlaidLink.resumeAfterTermination(...)`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Handled by the native SDKs; no Flutter-level resume method`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Closing Link`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PlaidLink.close()`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Handle `}<inlineCode parentName="td">{`onExit`}</inlineCode>{`; no Flutter-level close method`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`FinanceKit`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Not available`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`syncFinanceKit(...)`}</inlineCode>{` on iOS`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Flutter web`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Supported`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Not supported; use the `}<a parentName="td" {...{
              "href": "/docs/link/web"
            }}>{`Link Web SDK`}</a></td>
        </tr>
      </tbody>
    </table>
    <p>{`For example, opening standard Link with the community package uses global streams and static methods:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-dart",
        "metastring": "switcher=false #plaid_flutter (community)",
        "switcher": "false",
        "#plaid_flutter": true,
        "(community)": true
      }}>{`PlaidLink.onSuccess.listen((success) => handleSuccess(success));
PlaidLink.onExit.listen((exit) => handleExit(exit));
PlaidLink.onEvent.listen((event) => print(event.name));

await PlaidLink.create(
  configuration: LinkTokenConfiguration(token: linkToken),
);
await PlaidLink.open();
`}</code></pre>
    <p>{`The official SDK replaces this with a retained session that owns its own callbacks:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-dart",
        "metastring": "switcher=false #plaid_link_flutter (official)",
        "switcher": "false",
        "#plaid_link_flutter": true,
        "(official)": true
      }}>{`PlaidLinkSession? _linkSession;

Future<void> createLink(String linkToken) async {
  _linkSession = await createPlaidLinkSession(
    LinkTokenConfiguration(
      token: linkToken,
      onSuccess: handleSuccess,
      onExit: handleExit,
      onEvent: (event) => print(event.eventName),
    ),
  );
}

await _linkSession?.open();
`}</code></pre>
    <p>{`For the full migration, including Layer, Headless Link, Embedded Institution Search, FinanceKit, and a migration checklist, see the `}<a parentName="p" {...{
        "href": "https://github.com/plaid/plaid-link-flutter/blob/master/doc/migration-guide.md"
      }}>{`migration guide on GitHub`}</a>{`.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "requirements",
        level: 4,
        title: "Requirements",
      },
{
        id: "getting-started",
        level: 4,
        title: "Getting Started",
      },
{
        id: "opening-link",
        level: 4,
        title: "Opening Link",
      },
{
        id: "createplaidlinksession",
        level: 4,
        title: "createPlaidLinkSession()",
      },
{
        id: "open",
        level: 4,
        title: "open()",
      },
{
        id: "plaidlink",
        level: 4,
        title: "PlaidLink",
      },
{
        id: "onsuccess",
        level: 4,
        title: "onSuccess",
      },
{
        id: "onexit",
        level: 4,
        title: "onExit",
      },
{
        id: "onevent",
        level: 4,
        title: "onEvent",
      },
{
        id: "submit",
        level: 4,
        title: "submit()",
      },
{
        id: "dispose",
        level: 4,
        title: "dispose()",
      },
{
        id: "oauth",
        level: 4,
        title: "OAuth",
      },
{
        id: "upgrading",
        level: 4,
        title: "Upgrading",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
